import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { AL } from "../../al";
import { useUser } from "../../contexts/UserContext";
import "./Comments.css";

const CommentsPage = () => {
  const { id: chargeId } = useParams();
  const navigate = useNavigate();
  const { userInfo, token } = useUser();
  const inputRef = useRef(null);
  const commentsListRef = useRef(null);
  
  const [commentsHistory, setCommentsHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chargeDetails, setChargeDetails] = useState(null);
  const [sendingComment, setSendingComment] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const getCommentTimestamp = useCallback((comment) => {
    const dateValue = comment?.LastModifiedDate || comment?.CreatedDate;
    if (!dateValue) return 0;
    const timestamp = new Date(dateValue).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }, []);

  const fetchChargeDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/all-changes-comments?noticeId=${chargeId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
        }
      });
      const data = await res.json();
     
      if (data.records && data.records.length > 0) {
        setChargeDetails(data.records[0]); // store the first record only
      } else {
        setChargeDetails(null);
      }
    } catch (err) {
      console.error("Error fetching charge details:", err);
      setChargeDetails(null);
    }
  }, [chargeId]);
const handleGoBack = () => {
   navigate(-1); // go back to previous page
 };

    const handleLogout = () => {
        AL.logoutRedirect({
          post_logout_uri: window.location.origin + "/"
        });
      };

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-comments?charge_id=${chargeId}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
        }
      });
      const data = await res.json();

      const records = data?.compositeResponse?.[0]?.body?.records || [];
      const sortedRecords = [...records].sort(
        (a, b) => getCommentTimestamp(b) - getCommentTimestamp(a)
      );
      setCommentsHistory(sortedRecords);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setCommentsHistory([]);
    } finally {
      setLoading(false);
    }
  }, [chargeId, getCommentTimestamp]);

  useEffect(() => {
    if (chargeId) {
      fetchChargeDetails();
      fetchComments();
    }
  }, [chargeId, fetchChargeDetails, fetchComments]);

  useEffect(() => {
    const evaluateScrollState = () => {
      const el = commentsListRef.current;
      if (!el) {
        setIsScrollable(false);
        return;
      }
      setIsScrollable(el.scrollHeight > el.clientHeight);
    };

    evaluateScrollState();
    window.addEventListener("resize", evaluateScrollState);
    return () => window.removeEventListener("resize", evaluateScrollState);
  }, [commentsHistory, loading]);
  const handleAddComment = async () => {
    const value = inputRef.current.value.trim();
    if (!value) {
      alert("Please enter a comment");
      return;
    }

    setSendingComment(true);
    
    let currentUser = userInfo;
    let authToken = token;
    
    if (!currentUser || !authToken) {
      authToken = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      if (!authToken) {
        alert("Authentication token not found. Please login again.");
        setSendingComment(false);
        return;
      }

      try {
        const { jwtDecode } = await import("jwt-decode");
        const decoded = jwtDecode(authToken);
        currentUser = decoded.email;
      } catch (err) {
        console.error("Error decoding token:", err);
        alert("Failed to get user information. Please try again.");
        setSendingComment(false);
        return;
      }
    }

    if (!currentUser) {
      alert("User information not available. Please try again.");
      setSendingComment(false);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/add-comment`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`,
          "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
        },
        body: JSON.stringify({ chargeId, comments: value, UserName: currentUser}),
      });
      
      if (!res.ok) throw new Error("Failed to add comment");
      await res.json();
      inputRef.current.value = "";
      fetchComments();
    } catch (err) {
      console.error("Error adding comment:", err);
      alert("Failed to add comment");
    } finally {
      setSendingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCommentSource = (comment) => {
    const sentReceived = comment?.Sent_Received__c || "";
    return sentReceived.toLowerCase() === "received" ? "Vestas" : "Maersk";
  };

  const normalizeIdentity = (value) => String(value || "").trim().toLowerCase();

  const isCurrentUsersComment = (comment) => {
    const commentAuthor = normalizeIdentity(comment?.From__c);
    const currentUserIdentity = normalizeIdentity(userInfo);
    if (!commentAuthor || !currentUserIdentity) return false;
    return commentAuthor === currentUserIdentity;
  };

  const getCommentDirection = (comment) => {
    return isCurrentUsersComment(comment) ? "outgoing" : "incoming";
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const handleCommentInputKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!sendingComment) handleAddComment();
    }
  };

  return (
    <div className="comments-page">
      {/* Maersk TopBar */}
      <McTopBar
             product="Maersk | Unplanned Charges"
             productshort="Maersk"
             style={{
               paddingLeft: "20px",
               height: "64px",
               paddingRight: "20px",
               marginTop: "-5px",
               borderBottom: "1px solid #DBDBDB",
             }}
           >
     
             <div slot="actions" className="comments-page__topbar-actions">
               <McButton
                 onClick={handleGoBack}
                 label="Go Back"
                 appearance="primary"
                 fit="small"
                 style={{ marginRight: "1rem" }}
               />
               <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
             </div>
           </McTopBar>
      

      <div className="comments-page__content mds">
        <div className="comments-page__header">
          <h3 className="comments-page__title">Comments History</h3>
          <p className="comments-page__subtitle">
           Charge: <span>{chargeDetails?.Name || "Unknown Charge"}</span>
          </p>
        </div>

        <div className="comments-page__thread-card">
          <div className="comments-page__table-meta">
           <span>{commentsHistory.length} comment{commentsHistory.length === 1 ? "" : "s"}</span>
           <span>{isScrollable ? "Scroll for older comments • Newest first" : "Newest first"}</span>
          </div>
          {loading ? (
           <p className="comments-page__status">Loading comments...</p>
          ) : commentsHistory.length > 0 ? (
           <div className="comments-page__chat-list" ref={commentsListRef}>
             {commentsHistory.map((comment) => {
               const source = getCommentSource(comment);
               const user = comment.From__c || source || "Unknown";
               const direction = getCommentDirection(comment);
               return (
                 <div
                   key={comment.Id || `${comment.LastModifiedDate}-${comment.Description__c}`}
                   className={`comments-page__message comments-page__message--${direction}`}
                 >
                   <div className="comments-page__message-avatar" aria-hidden="true">
                     {getInitials(user)}
                   </div>
                   <div className="comments-page__bubble">
                     <div className="comments-page__bubble-meta">
                       <span className="comments-page__bubble-user">{user}</span>
                       <span className="comments-page__bubble-source">{source}</span>
                       <span className="comments-page__bubble-time">
                         {formatDate(comment.LastModifiedDate || comment.CreatedDate)}
                       </span>
                     </div>
                     <p className="comments-page__bubble-text">{comment.Description__c || "-"}</p>
                   </div>
                 </div>
               );
             })}
           </div>
          ) : (
           <p className="comments-page__status">No comments found.</p>
          )}
        </div>

        {/* Add Comment Section */}
        <div className="comments-page__composer">
          <label htmlFor="new-comment" className="comments-page__composer-label">
           Add Comment
          </label>
          <textarea
           ref={inputRef}
           id="new-comment"
           placeholder="Write your comment..."
           className="comments-page__input"
           disabled={sendingComment}
           rows={3}
           onKeyDown={handleCommentInputKeyDown}
          />
          <p className="comments-page__composer-help">
           Press <b>Enter</b> to send, <b>Shift + Enter</b> for a new line.
          </p>
          <div className="comments-page__composer-actions">
           <McButton
             label={sendingComment ? "Sending..." : "Send"}
             appearance="primary"
             onClick={handleAddComment}
             disabled={sendingComment}
           />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsPage;
